/**
 * Customer-facing outbound hrefs. Only http(s) survive, and Barracuda
 * LinkProtect wrappers unwrap to the destination so a pasted email
 * "safe link" does not send the customer through linkprotect.cudasvc.com.
 */

const BARRACUDA_LINKPROTECT_HOST = "linkprotect.cudasvc.com";

function isHttpUrl(url: URL): boolean {
  return url.protocol === "http:" || url.protocol === "https:";
}

function isBarracudaLinkProtectHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return (
    host === BARRACUDA_LINKPROTECT_HOST ||
    host.endsWith(`.${BARRACUDA_LINKPROTECT_HOST}`)
  );
}

function parseHttpUrl(raw: string): URL | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const withProto = /^\w+:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const url = new URL(withProto);
    return isHttpUrl(url) ? url : null;
  } catch {
    return null;
  }
}

function unwrapBarracudaDestination(url: URL): string | null {
  if (!isBarracudaLinkProtectHost(url.hostname)) return null;
  const dest = url.searchParams.get("a")?.trim();
  return dest || null;
}

/**
 * Normalize a stored button / CTA URL for the public renderer.
 * Returns null for blank, non-http(s), or a wrapper whose destination
 * is not a safe http(s) URL.
 */
export function safeExternalHref(raw: string): string | null {
  const first = parseHttpUrl(raw);
  if (!first) return null;

  const wrapped = unwrapBarracudaDestination(first);
  if (!wrapped) return first.href;

  const dest = parseHttpUrl(wrapped);
  if (!dest) return null;
  if (isBarracudaLinkProtectHost(dest.hostname)) {
    const nested = unwrapBarracudaDestination(dest);
    if (!nested) return null;
    const inner = parseHttpUrl(nested);
    return inner ? inner.href : null;
  }
  return dest.href;
}
